import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import Logo from '../components/Logo';
import ThemeToggle from '../components/ThemeToggle';
import Footer from '../components/Footer';
import { Mail, Lock, Building2, Wrench, Loader2, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { CONTRACTOR_TRADES } from '../lib/utils';

const Signup = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { signup } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        company_name: '',
        contractor_trade: '',
        invite_code: searchParams.get('code') || ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleTradeChange = (value) => {
        setFormData({ ...formData, contractor_trade: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        if (!formData.contractor_trade) {
            toast.error('Please select your trade');
            return;
        }

        if (!formData.invite_code.trim()) {
            toast.error('Invite code is required during beta');
            return;
        }

        setLoading(true);

        try {
            await signup({
                email: formData.email,
                password: formData.password,
                company_name: formData.company_name,
                contractor_trade: formData.contractor_trade,
                invite_code: formData.invite_code.trim().toUpperCase()
            });
            toast.success('Account created! Check your email to verify your account.');
            navigate('/dashboard');
        } catch (err) {
            toast.error(err.message || 'Signup failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Theme Toggle - Top Right */}
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {/* Logo */}
                    <Link to="/" className="flex items-center justify-center mb-8">
                        <Logo variant="full" size="large" />
                    </Link>

                    <Card className="bg-card border-border">
                        <CardHeader className="text-center">
                            <CardTitle className="font-heading font-bold text-2xl text-foreground">
                                Create Account
                            </CardTitle>
                            <CardDescription>
                                Join the contractor network and start checking addresses
                            </CardDescription>
                            <div className="mt-2 px-4 py-2 bg-primary/10 rounded-lg">
                                <p className="text-xs text-primary font-medium">
                                    Private Beta - Invite code required
                                </p>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Invite Code - Required during beta */}
                                <div className="space-y-2">
                                    <Label htmlFor="invite_code">Invite Code *</Label>
                                    <div className="relative">
                                        <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="invite_code"
                                            name="invite_code"
                                            type="text"
                                            placeholder="Enter your invite code"
                                            value={formData.invite_code}
                                            onChange={handleChange}
                                            className="pl-10 bg-accent border-border h-12 uppercase"
                                            required
                                            data-testid="signup-invite-code-input"
                                        />
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Don't have a code? Ask an existing member for one.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="company_name">Company Name</Label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="company_name"
                                            name="company_name"
                                            type="text"
                                            placeholder="Your Company LLC"
                                            value={formData.company_name}
                                            onChange={handleChange}
                                            className="pl-10 bg-accent border-border h-12"
                                            required
                                            data-testid="signup-company-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="contractor_trade">Trade / Specialty</Label>
                                    <div className="relative">
                                        <Select onValueChange={handleTradeChange} value={formData.contractor_trade}>
                                            <SelectTrigger 
                                                className="h-12 bg-accent border-border"
                                                data-testid="signup-trade-select"
                                            >
                                                <Wrench className="h-4 w-4 text-muted-foreground mr-2" />
                                                <SelectValue placeholder="Select your trade" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {CONTRACTOR_TRADES.map((trade) => (
                                                    <SelectItem key={trade} value={trade}>
                                                        {trade}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="contractor@example.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="pl-10 bg-accent border-border h-12"
                                            required
                                            data-testid="signup-email-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="pl-10 bg-accent border-border h-12"
                                            required
                                            data-testid="signup-password-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type="password"
                                            placeholder="••••••••"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="pl-10 bg-accent border-border h-12"
                                            required
                                            data-testid="signup-confirm-password-input"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full h-12 text-base active:scale-95 transition-transform"
                                    disabled={loading}
                                    data-testid="signup-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creating account...
                                        </>
                                    ) : (
                                        'Create Account'
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 text-center">
                                <p className="text-muted-foreground">
                                    Already have an account?{' '}
                                    <Link 
                                        to="/login" 
                                        className="text-primary hover:underline font-medium"
                                        data-testid="signup-login-link"
                                    >
                                        Sign in
                                    </Link>
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
            
            {/* Footer */}
            <Footer />
        </div>
    );
};

export default Signup;
